/**
 * ALL CAPS — Find Your Cap quiz controller.
 * Question/answer/scoring content lives entirely in all-caps-quiz-data.js
 * (window.ACQuizData) — this file only runs the quiz flow and the matching
 * engine against real Shopify product data.
 *
 * Initialisation is root-scoped (not a single fragile querySelector) and
 * idempotent, so it works both on normal page load and when Shopify's
 * theme editor reloads this section (shopify:section:load).
 */
(function () {
  function initAllCapsQuiz(root) {
    if (!root || root.dataset.acQuizInitialized === 'true') return;
    root.dataset.acQuizInitialized = 'true';

    const sectionId = root.id.replace(/^ac-quiz-/, '');
    const shellEl = document.getElementById(`ac-quiz-shell-${sectionId}`);
    const questionWrap = document.getElementById(`ac-question-wrap-${sectionId}`);
    const questionTitle = document.getElementById(`ac-question-title-${sectionId}`);
    const questionText = document.getElementById(`ac-question-text-${sectionId}`);
    const answersEl = document.getElementById(`ac-answers-${sectionId}`);
    const progressFill = document.getElementById(`ac-progress-fill-${sectionId}`);
    const resultCard = document.getElementById(`ac-result-card-${sectionId}`);
    const resultImage = document.getElementById(`ac-result-image-${sectionId}`);
    const resultName = document.getElementById(`ac-result-name-${sectionId}`);
    const resultDesc = document.getElementById(`ac-result-desc-${sectionId}`);
    const resultTag = document.getElementById(`ac-result-tag-${sectionId}`);
    const resultPrice = document.getElementById(`ac-result-price-${sectionId}`);
    const resultAdd = document.getElementById(`ac-result-add-${sectionId}`);
    const resultAlts = document.getElementById(`ac-result-alts-${sectionId}`);
    const quizHint = document.getElementById(`ac-quiz-hint-${sectionId}`);
    const toast = document.getElementById(`ac-toast-${sectionId}`);
    const restartButtons = root.querySelectorAll('[data-ac-restart]');

    // No shell on this page (enable_quiz off, or markup missing) — nothing to run.
    if (!shellEl || !questionWrap || !questionTitle || !answersEl) return;

    // Visible failure states instead of a silent blank card, so a real problem
    // (missing data script, empty product pool, quiz data not loaded) is
    // immediately obvious in the DOM rather than indistinguishable from "still
    // loading" or a CSS issue.
    function showError(message) {
      questionTitle.textContent = 'Find Your Cap is unavailable right now';
      questionText.textContent = message;
      answersEl.innerHTML = '';
      console.error('[ALL CAPS quiz]', message, { sectionId });
    }

    // QUIZ DATA vs PRODUCT DATA are deliberately independent. Missing/broken
    // quiz data (script failed to load, no questions) is a real blocker — the
    // quiz literally cannot run without it. A missing/empty PRODUCT pool is
    // NOT a blocker: the quiz still runs question-to-question exactly the
    // same either way. Product availability is only checked once, at
    // showResult(), where a genuine "nothing eligible" fallback is shown
    // instead of a product — see showResult() below.
    if (!window.ACShared) {
      showError('ac-shared.js did not load — check the network tab for a 404 on that asset.');
      return;
    }
    if (!window.ACQuizData || !Array.isArray(window.ACQuizData.questions) || !window.ACQuizData.questions.length) {
      showError('all-caps-quiz-data.js did not load or contained no questions.');
      return;
    }

    const products = window.ACShared.readProductData(`ac-quiz-data-${sectionId}`);
    const questions = window.ACQuizData.questions;
    const axisWeights = window.ACQuizData.axisWeights || {};
    const resultCount = Math.max(1, parseInt(shellEl.dataset.acResultCount, 10) || 1);

    let currentQ = 0;
    let scores = {}; // { category: { office: 3 }, tone: { dry: 2 }, ... }
    let resultProduct = null;

    function addScores(delta) {
      Object.keys(delta).forEach((axis) => {
        scores[axis] = scores[axis] || {};
        Object.keys(delta[axis]).forEach((value) => {
          scores[axis][value] = (scores[axis][value] || 0) + delta[axis][value];
        });
      });
    }

    // Score every real product against the accumulated quiz profile. Category
    // and traits are treated the same way — this loop has no idea "office" or
    // "dry" exist, it just compares whatever axes the product happens to carry.
    function scoreProduct(product) {
      let total = 0;
      const productAxisValues = Object.assign({ category: product.category }, product.traits);
      Object.keys(productAxisValues).forEach((axis) => {
        const value = productAxisValues[axis];
        if (!value) return;
        const axisScores = scores[axis];
        const points = axisScores && axisScores[value] ? axisScores[value] : 0;
        const weight = axisWeights[axis] != null ? axisWeights[axis] : 1;
        total += points * weight;
      });
      return total;
    }

    function rankedProducts() {
      return products
        .filter((p) => p.available)
        .map((p) => ({ product: p, score: scoreProduct(p) }))
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return a.product.id - b.product.id; // deterministic tie-break
        })
        .map((entry) => entry.product);
    }

    function renderQuestion() {
      const q = questions[currentQ];
      questionTitle.textContent = q.title;
      questionText.textContent = q.text;
      answersEl.innerHTML = '';
      q.answers.forEach((a) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ac-answer';
        btn.innerHTML = `<strong>${window.ACShared.escapeHtml(a.label)}</strong><small>${window.ACShared.escapeHtml(a.sub)}</small>`;
        btn.addEventListener('click', () => answerQuestion(a.scores));
        answersEl.appendChild(btn);
      });
      if (progressFill) progressFill.style.width = `${((currentQ + 1) / questions.length) * 100}%`;
    }

    function answerQuestion(scoreDelta) {
      addScores(scoreDelta);
      currentQ++;
      if (currentQ < questions.length) {
        renderQuestion();
      } else {
        showResult();
      }
    }

    function renderAlternates(list) {
      if (!resultAlts) return;
      if (!list.length) {
        resultAlts.innerHTML = '';
        resultAlts.hidden = true;
        return;
      }
      resultAlts.hidden = false;
      resultAlts.innerHTML = `<span class="ac-alts-label">Also worth a look</span>` +
        list
          .map(
            (p) => `
          <a class="ac-alt" href="${p.url}">
            <span class="ac-alt-media">${window.ACShared.capSVG(p)}</span>
            <span class="ac-alt-title">${window.ACShared.escapeHtml(p.title)}</span>
          </a>`
          )
          .join('');
    }

    function showResult() {
      const ranked = rankedProducts();
      resultProduct = ranked[0] || products[0] || null;
      const alternates = resultProduct ? ranked.slice(1, resultCount) : [];

      questionWrap.style.display = 'none';
      if (resultCard) {
        resultCard.classList.add('ac-show');
        if (resultProduct) {
          // Normal path: a real, eligible Shopify product matched the quiz profile.
          resultImage.innerHTML = window.ACShared.capSVG(resultProduct);
          resultImage.href = resultProduct.url || '#';
          resultImage.setAttribute('aria-label', resultProduct.title);
          resultName.textContent = resultProduct.title;
          resultDesc.textContent = resultProduct.description;
          resultTag.textContent = resultProduct.category || '—';
          resultPrice.textContent = resultProduct.price_formatted;
          renderAlternates(alternates);
          resultAdd.hidden = false;
          resultAdd.disabled = !resultProduct.available;
          resultAdd.textContent = resultProduct.available ? resultAdd.dataset.defaultLabel || resultAdd.textContent : 'Sold out';
        } else {
          // Genuine fallback: the quiz completed fine, there's just nothing
          // eligible in the store's product pool right now. This is the ONLY
          // place product availability affects the visitor — the questions
          // themselves never cared.
          resultImage.innerHTML = '';
          resultImage.removeAttribute('href');
          resultName.textContent = 'New caps coming soon';
          resultDesc.textContent = "We couldn't find an eligible cap for that combination just yet — check back soon or browse everything we've got.";
          resultTag.textContent = '';
          resultPrice.textContent = '';
          renderAlternates([]);
          resultAdd.hidden = true;
        }
      }
      if (quizHint) {
        quizHint.textContent = resultProduct
          ? 'This recommendation format could be brilliant for gifting, browsing and social sharing.'
          : 'Use "Skip to shop" above to browse everything available right now.';
      }
    }
    if (resultAdd) resultAdd.dataset.defaultLabel = resultAdd.textContent;

    function restartQuiz() {
      currentQ = 0;
      scores = {};
      resultProduct = null;
      questionWrap.style.display = 'block';
      if (resultCard) resultCard.classList.remove('ac-show');
      renderQuestion();
    }

    function showToast(message) {
      if (!toast) return;
      toast.textContent = message;
      toast.classList.add('ac-show');
      setTimeout(() => toast.classList.remove('ac-show'), 1300);
    }

    if (resultAdd) {
      resultAdd.addEventListener('click', () => {
        if (!resultProduct || !resultProduct.available || !resultProduct.variant_id) return;
        resultAdd.setAttribute('aria-disabled', 'true');
        window.ACShared.addToCart(resultProduct.variant_id, {
          onSuccess: () => {
            showToast(`${resultProduct.title.toUpperCase()} ADDED`);
            resultAdd.removeAttribute('aria-disabled');
          },
          onError: (message) => {
            showToast(message || 'Could not add to bag');
            resultAdd.removeAttribute('aria-disabled');
          },
        });
      });
    }

    restartButtons.forEach((btn) => btn.addEventListener('click', restartQuiz));

    renderQuestion();
  }

  function initAll() {
    document.querySelectorAll('[data-ac-quiz]').forEach(initAllCapsQuiz);
  }

  // Normal storefront load.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor: re-run when this section (or the page) reloads.
  // initAllCapsQuiz() itself is idempotent (dataset.acQuizInitialized guard),
  // so this never double-attaches listeners to an already-live quiz — it only
  // does real work for a freshly-reloaded section's new DOM node.
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector ? event.target.querySelector('[data-ac-quiz]') : null;
    if (root) initAllCapsQuiz(root);
  });
})();
